/**
 * The local host (D156): the bundle in the browser, the turn run here.
 *
 * @remarks
 * This is `play: 'client'` — what the shell did before D156, moved behind
 * the host contract: the engine's `processPlayerInput` on a state this
 * object holds, R10's automatic persistence (every committed state schedules
 * a debounced write of the WHOLE future — D23: the state carries the
 * scheduler queue and the RNG, so the snapshot owes nothing else), and the
 * resume rule from `bootSession` (`?seed=` outranks a save, because a
 * marketing link or a bug report must reproduce).
 *
 * The clock is a parameter: `seedOf` gives a fresh session its seed and
 * `savedAtIso` stamps a snapshot, and both are the app edge's to supply —
 * this package owns no nondeterminism. The host also lets one listener
 * watch its commits, which is how the analytics reporter (D154) sees the
 * `(before, after)` pair its tally diff needs without the state ever
 * reaching the shell's store.
 *
 * **The port, here (D164 on D163).** A save carries the command list already
 * — `session.commandHistory` is every input verbatim — and, once published
 * under a hash, the hash it was played on. When the host was made for a
 * NEWER hash than the save names, and the boot can fetch the old bundle by
 * its hash (the same immutable door it fetched the new one through), the
 * save opens against the version it was played on and the same pure
 * `portSession` the platform runs decides the rest: every command lands as
 * it did and the session moves in silence; one does not and the page keeps
 * running the OLD version with the report on the open, until the player
 * answers — update swaps the bundle in place from the last matching
 * command, stay records the declined hash so the next open does not ask.
 * A fresh start always begins on the newest. An old bundle the boot cannot
 * reach (Mike's ruling): the save opens on the new version, as it always
 * did — best effort, nothing lost that was not already at risk.
 *
 * **The Record, here (D241).** With a `recordStorage`, the host reads the
 * game's Record once at `open` and holds it: a fresh run (an open with no
 * save, a `restart`) begins on it, and a resumed save keeps the view its run
 * began with. After each committed turn the layers the turn wrote
 * (`recordedLayersOf`) fold into the held Record and go to the adapter —
 * which is the device, or the account, as the shell decides. `forgetRecord`
 * empties both. A read or a write that fails costs that read or that write,
 * never the session.
 *
 * @packageDocumentation
 */


import { type PortReport, type TurnOutcome } from '@orpheus/api-contracts';
import { type LoadedGameBundle } from '@orpheus/content-loader';
import { mergeRecordLayers, type RecordLayer, type RuntimeState, type SaveSnapshot } from '@orpheus/content-schema';
import { answerLoadFoundNothing, buildSaveSnapshot, openSaveSnapshot, processPlayerInput, readSaveOrigin, recordedLayersOf, serializeTranscript } from '@orpheus/engine-runtime';

import { deriveSessionView, deriveTurnOutcome, genesisOf, nextStateOf } from './derive-session-view.js';
import { describeGame } from './describe-game.js';
import { type OpenedSession, type PlayHost, type PortChoice } from './play-host.js';
import { portSession } from './port-session.js';
import { readRecordSafely, type RecordStorageAdapter } from './record-storage.js';

/** WHERE snapshots live — the app supplies this; the host never knows. */
export interface SaveStorageAdapter {
	/** Read back whatever was last written for this game, if anything (unknown-shaped: the pure opener validates). */
	readSnapshot(bundleId: string): Promise<unknown>;
	/** Persist one snapshot, replacing any previous one for its `bundleId`. */
	writeSnapshot(snapshot: SaveSnapshot): Promise<void>;
}

/** One committed state, announced to whoever is watching — with the bundle it ran on, which a port can change (D164). */
export type HostCommit =
	| { readonly after: RuntimeState; readonly before: RuntimeState; readonly bundle: LoadedGameBundle; readonly kind: 'turn' }
	| { readonly bundle: LoadedGameBundle; readonly kind: 'opened'; readonly state: RuntimeState; readonly wasResumed: boolean; readonly wasSeeded: boolean }
	| { readonly bundle: LoadedGameBundle; readonly kind: 'restarted'; readonly state: RuntimeState };

/** What the local host needs from its edge. */
export interface LocalHostOptions {
	/** Quiet time before an autosave write; a burst of turns costs one write. Defaults to 750ms. */
	readonly autosaveDebounceMs?: number;
	readonly bundle: LoadedGameBundle;
	/** The published hash `bundle` was fetched by (D164). Absent, the pack was never published under one, and never ports. */
	readonly bundleHash?: string;
	/** The bundle a save was played on, by its hash (D164) — the boot's immutable door. Absent, a save from another version opens on this one. */
	readonly bundleOf?: (hash: string) => Promise<LoadedGameBundle>;
	/** D241: where the game's Record is kept between runs. Absent, every run begins with none and nothing is remembered. */
	readonly recordStorage?: RecordStorageAdapter;
	/** The stamp for a snapshot's `savedAtIso` — display metadata the pure builder refuses to invent. */
	readonly savedAtIso: () => string;
	/** Where saves live. Absent, nothing persists and nothing resumes (a test, a preview). */
	readonly saveStorage?: SaveStorageAdapter;
	/** An explicit seed — `?seed=` — which outranks any save. */
	readonly seed?: number;
	/** The seed for a fresh session when none was given. */
	readonly seedOf: () => number;
}

/** A local host: the contract, plus the commit stream only a local host has. */
export interface LocalHost extends PlayHost {
	/** Watch every committed state. Returns the unsubscribe. */
	subscribe(listener: (commit: HostCommit) => void): () => void;
}

/** One version of the game as the host can run it: the bundle, and the hash it is published under when it is. */
interface Edition {
	readonly bundle: LoadedGameBundle;
	readonly hash?: string;
}

/** What a save resumed into: the edition it runs on, the state, and the port it is being asked about, if any. */
interface Resumption {
	readonly edition: Edition;
	readonly report?: PortReport;
	readonly state: RuntimeState;
}

/**
 * Create a host that runs the game here, on this bundle.
 *
 * @param options - {@link LocalHostOptions}
 * @returns The host
 */
export const createLocalHost = (options: LocalHostOptions): LocalHost => {
	/** The version the host was made for — what a fresh start and a full port run on. */
	const latest: Edition = { bundle: options.bundle, ...(options.bundleHash === undefined ? {} : { hash: options.bundleHash }) };
	const listeners = new Set<(commit: HostCommit) => void>();
	/** The version the session is running on — `latest`, or the one its save was played on while a port is unanswered or declined. */
	let active: Edition = latest;
	/** Whether the resume found a newer version the session could not follow silently, and the player has not answered. */
	let isPortOffered = false;
	/** The newer version the player chose to stay away from — written into every save from here on. */
	let declinedBundleHash: string | undefined;
	let state: RuntimeState | undefined;
	let pendingWrite: ReturnType<typeof setTimeout> | undefined;
	/** D241: the game's Record as the host holds it — read at `open`, grown by every layer a turn writes. */
	let heldRecord: readonly RecordLayer[] = [];
	const { gameId } = latest.bundle.runtimeManifest;

	const announce = (commit: HostCommit): void => {
		listeners.forEach((listener) => {
			listener(commit);
		});
	};

	/** The one slot's snapshot of a state, stamped with where the session stands on the versions. */
	const snapshotOf = (committed: RuntimeState): SaveSnapshot =>
		buildSaveSnapshot(active.bundle, committed, {
			origin: {
				...(active.hash === undefined ? {} : { bundleHash: active.hash }),
				...(declinedBundleHash === undefined ? {} : { declinedBundleHash }),
			},
			savedAtIso: options.savedAtIso(),
		});

	/**
	 * Write the slot now. A failed write loses one write, never the session —
	 * surfacing storage errors to the player is the shell's concern, not the
	 * host's.
	 */
	const writeSlot = (committed: RuntimeState): void => {
		options.saveStorage?.writeSnapshot(snapshotOf(committed)).catch(() => undefined);
	};

	/**
	 * Debounce by cancellation: only the LAST commit in a burst writes. D179:
	 * a `manual` pack keeps nothing until the player says SAVE — the
	 * automatic write is what the mode switches off, and nothing else.
	 */
	const scheduleAutosave = (committed: RuntimeState): void => {
		if (options.saveStorage === undefined || active.bundle.contentPack.manifest.saves === 'manual') {
			return;
		}

		if (pendingWrite !== undefined) {
			clearTimeout(pendingWrite);
		}

		pendingWrite = setTimeout(() => {
			pendingWrite = undefined;
			writeSlot(committed);
		}, options.autosaveDebounceMs ?? 750);
	};

	const commit = (next: RuntimeState): RuntimeState => {
		state = next;
		scheduleAutosave(next);

		return next;
	};

	/** The state the host holds, which exists only once the session is open. */
	const stateOrThrow = (): RuntimeState => {
		if (state === undefined) {
			throw new Error('the session is not open — call open() first');
		}

		return state;
	};

	const opened = (committed: RuntimeState, seed: number, wasResumed: boolean, report?: PortReport): OpenedSession => ({
		game: describeGame(active.bundle),
		...(report === undefined ? {} : { pendingPort: report }),
		seed,
		view: deriveSessionView(active.bundle, committed),
		wasResumed,
	});

	/**
	 * The edition a save was played on, when it is another than `latest` and
	 * the boot can fetch it; `latest` otherwise — including when the fetch
	 * fails, which opens the save on the new version as it always did.
	 */
	const playedEditionOf = async (bundleHash: string | undefined): Promise<Edition> => {
		if (bundleHash === undefined || latest.hash === undefined || bundleHash === latest.hash || options.bundleOf === undefined) {
			return latest;
		}

		try {
			return { bundle: await options.bundleOf(bundleHash), hash: bundleHash };
		} catch {
			return latest;
		}
	};

	/** The replay against `latest` from where the session stands: the commands are the state's own. */
	const portedToLatest = (from: Edition, current: RuntimeState, latestHash: string) =>
		portSession({
			commands: current.session.commandHistory,
			from: from.bundle,
			// D241: the Record the session began on is one of its inputs, like the seed.
			record: current.record.heldAtGenesis,
			seed: current.randomness.sessionSeed,
			to: latest.bundle,
			toBundleHash: latestHash,
		});

	/** D241: keep what one turn wrote into the Record — held here at once, and handed to the adapter. */
	const keepRecordedLayers = (layers: readonly RecordLayer[]): void => {
		if (layers.length === 0) {
			return;
		}

		heldRecord = mergeRecordLayers(heldRecord, layers);
		options.recordStorage?.appendLayers(gameId, layers).catch(() => undefined);
	};

	/** Resume from storage when nothing outranks it; `undefined` means begin fresh. */
	const resumed = async (): Promise<Resumption | undefined> => {
		if (options.seed !== undefined || options.saveStorage === undefined) {
			return undefined;
		}

		const raw = await options.saveStorage.readSnapshot(gameId);
		const origin = readSaveOrigin(raw);
		const played = await playedEditionOf(origin?.bundleHash);
		const outcome = openSaveSnapshot(raw, played.bundle);

		if (outcome.type !== 'opened') {
			return undefined;
		}

		if (played === latest || latest.hash === undefined) {
			return { edition: latest, state: outcome.state };
		}

		// Declined before: the session stays on the version it chose, silently, until a newer one than THAT is published.
		if (origin?.declinedBundleHash === latest.hash) {
			declinedBundleHash = latest.hash;

			return { edition: played, state: outcome.state };
		}

		const port = portedToLatest(played, outcome.state, latest.hash);

		return port.kind === 'full' ? { edition: latest, state: port.state } : { edition: played, report: port.report, state: outcome.state };
	};

	/**
	 * D179: a `manual` pack's SAVE and LOAD. The engine has said its line and
	 * committed the words to the transcript; the host does the thing. SAVE
	 * writes the slot with the state the engine just produced. LOAD opens
	 * the slot and, when it holds a save this edition can play, that save is
	 * the next state — the transcript with it, so the words said since are
	 * gone the way the world is. Nothing in the slot, or one that will not open,
	 * keeps the engine's state: the line it spoke was phrased for exactly
	 * that.
	 *
	 * @param verdict - The engine's verdict on the input
	 * @returns The state to commit
	 */
	const honoringHostRequest = async (verdict: ReturnType<typeof processPlayerInput>, before: RuntimeState, input: string): Promise<RuntimeState> => {
		const next = nextStateOf(verdict);

		if (verdict.type !== 'reaction' || verdict.hostRequest === undefined) {
			return next;
		}

		if (verdict.hostRequest === 'save') {
			writeSlot(next);

			return next;
		}

		const raw = await options.saveStorage?.readSnapshot(active.bundle.runtimeManifest.gameId);
		const outcome = openSaveSnapshot(raw, active.bundle);

		// D185: the engine answered `loadRestored` without knowing whether a
		// slot exists; only the host does. Nothing to bring back means the
		// honest answer to the same input is the other one — committed from
		// the state the engine answered from, so the transcript carries one
		// echo of `load` and the line that is true.
		return outcome.type === 'opened' ? outcome.state : nextStateOf(answerLoadFoundNothing(active.bundle, before, input));
	};

	return {
		choosePort: async (choice: PortChoice) => {
			if (!isPortOffered || latest.hash === undefined) {
				return Promise.reject(new Error('no port is pending — the session is on the version it loaded'));
			}

			const current = stateOrThrow();

			isPortOffered = false;

			if (choice === 'stay') {
				declinedBundleHash = latest.hash;
				// Nothing about the state changed; the save must still say so.
				scheduleAutosave(current);

				return Promise.resolve(opened(current, current.randomness.sessionSeed, true));
			}

			// Recomputed from where the session stands NOW: turns played since the offer are part of the cost.
			const port = portedToLatest(active, current, latest.hash);

			active = latest;
			declinedBundleHash = undefined;

			const committed = commit(port.state);

			return Promise.resolve(opened(committed, committed.randomness.sessionSeed, true));
		},
		exportTranscript: async () => Promise.resolve(serializeTranscript(active.bundle, stateOrThrow())),
		// D241: forgotten here at once, and wherever the adapter keeps it; the run in progress keeps the view it began with.
		forgetRecord: async () => {
			heldRecord = [];
			await options.recordStorage?.forgetRecord(gameId);
		},
		open: async () => {
			heldRecord = await readRecordSafely(options.recordStorage, gameId);

			const restored = await resumed();

			active = restored?.edition ?? latest;
			isPortOffered = restored?.report !== undefined;

			// The seed as the caller would quote it — the URL's, or the clock's —
			// or, resumed, the one the save was running on; under a `fixed` policy,
			// the pack's own either way.
			const drawn = options.seed ?? options.seedOf();
			const committed = commit(restored?.state ?? genesisOf(active.bundle, drawn, heldRecord));
			const seed = restored === undefined && active.bundle.contentPack.manifest.seedPolicy.type !== 'fixed' ? drawn : committed.randomness.sessionSeed;

			announce({ bundle: active.bundle, kind: 'opened', state: committed, wasResumed: restored !== undefined, wasSeeded: options.seed !== undefined });

			return opened(committed, seed, restored !== undefined, restored?.report);
		},
		restart: async () => {
			// A fresh start is on the newest version, whatever the old session was answering.
			active = latest;
			isPortOffered = false;
			declinedBundleHash = undefined;

			const drawn = options.seedOf();
			const committed = commit(genesisOf(active.bundle, drawn, heldRecord));

			announce({ bundle: active.bundle, kind: 'restarted', state: committed });

			return Promise.resolve(opened(committed, active.bundle.contentPack.manifest.seedPolicy.type === 'fixed' ? committed.randomness.sessionSeed : drawn, false));
		},
		submit: async (input: string): Promise<TurnOutcome> => {
			const before = stateOrThrow();
			const verdict = processPlayerInput(active.bundle, before, input);
			const after = commit(await honoringHostRequest(verdict, before, input));

			// D241: read off the ENGINE's next state, never a slot a LOAD brought back — what this turn wrote, and only that.
			keepRecordedLayers(recordedLayersOf(before, nextStateOf(verdict)));
			announce({ after, before, bundle: active.bundle, kind: 'turn' });

			return deriveTurnOutcome(active.bundle, verdict, after);
		},
		subscribe: (listener) => {
			listeners.add(listener);

			return () => {
				listeners.delete(listener);
			};
		},
	};
};
